import {
  CENTRO_DE_CUSTO,
  TIPO_LANCAMENTO,
  type CentroDeCusto,
  type FormaPagamento,
  type TipoLancamento,
} from '@arena/shared/dominio';
import type { Acao, Recurso } from '@arena/shared/permissoes';
import {
  efeitoNoCaixa,
  FILTROS_DE_ORIGEM,
  FORMAS_DO_LANCAMENTO_MANUAL,
  ROTULO_DA_ORIGEM,
  ROTULO_FREQUENCIA,
  zCancelamentoDeLancamento,
  zEstornoDeLancamento,
  zId,
  zMes,
  type AlteracaoDeLancamento,
  type CategoriaDoPlano,
  type ConsultaDoExtrato,
  type DetalheDoLancamento,
  type FiltroDeOrigem,
  type LancamentoNoExtrato,
  type OrigemLancamento,
  type RealizacaoDeLancamento,
  type RecorrenciaResumida,
  type ResultadoDoCentro,
} from '@arena/shared/schemas';
import { dataValida } from '@arena/shared/tempo';
import { dinheiro } from '../agenda/formatos.js';
import { dataCompleta } from '../alunos/formatos.js';
import type { Situacao } from '../ui/primitivos.js';

/**
 * O que a tela do financeiro decide sozinha. Funções puras — nenhuma toca
 * a rede nem o React.
 *
 * O mês e os filtros moram na URL (`?mes=2026-09&tipo=Saida&centro=Bar`),
 * como em `#v-planos`: o link colado abre o mesmo recorte, e o F5 não
 * perde onde a pessoa estava. Quem LÊ a URL é daqui, e ignora o que o
 * servidor recusaria — link velho ou digitado à mão vira "sem filtro" na
 * tela em vez de um 400 na cara de quem o colou.
 *
 * O detalhe de um lançamento (`?lancamento=<id>`) também é daqui: que ações
 * existem em cada estado, o que cada uma manda ao servidor, e o texto que
 * explica de onde veio o lançamento automático.
 */

// ---------------------------------------------------------------------
// O mês
// ---------------------------------------------------------------------

/** O mês de `?mes=`, ou `undefined` se não há ou se não é um mês de verdade. */
export function lerMesDaUrl(params: URLSearchParams): string | undefined {
  const mes = zMes.safeParse(params.get('mes'));
  return mes.success ? mes.data : undefined;
}

/**
 * Põe o mês na URL, no padrão da agenda (`irPara`): o mês corrente é o
 * padrão e não ocupa a URL. Um mês que não existe também não — o parâmetro
 * sai, em vez de ir para um endereço que o servidor vai recusar.
 */
export function definirMes(params: URLSearchParams, mes: string, mesAtual: string): void {
  if (!zMes.safeParse(mes).success || mes === mesAtual) params.delete('mes');
  else params.set('mes', mes);
}

// ---------------------------------------------------------------------
// Os filtros do extrato
// ---------------------------------------------------------------------

/** Linhas por página do extrato; "Carregar mais" traz a seguinte. */
export const LIMITE_DO_EXTRATO = 25;

/**
 * Os filtros que a URL guarda: `?tipo=&centro=&categoria=&origem=`. O
 * servidor os chama de `centroDeCusto` e `categoriaId`; a URL é a cara da
 * tela, e `consultaDoExtrato` faz a tradução.
 */
export interface FiltrosDoExtrato {
  readonly tipo?: TipoLancamento;
  readonly centroDeCusto?: CentroDeCusto;
  readonly categoriaId?: string;
  readonly origem?: FiltroDeOrigem;
}

const CHAVES_DOS_FILTROS = ['tipo', 'centro', 'categoria', 'origem'] as const;

/** Uma chave de filtro na URL — o `definirFiltro` não aceita outra. */
export type ChaveDeFiltro = (typeof CHAVES_DOS_FILTROS)[number];

/** Lê os filtros da URL; cada um que não presta cai sozinho, sem derrubar os outros. */
export function lerFiltrosDoExtrato(params: URLSearchParams): FiltrosDoExtrato {
  const tipo = TIPO_LANCAMENTO.find((t) => t === params.get('tipo'));
  const centroDeCusto = CENTRO_DE_CUSTO.find((c) => c === params.get('centro'));
  const categoria = zId.safeParse(params.get('categoria'));
  const origem = FILTROS_DE_ORIGEM.find((o) => o === params.get('origem'));
  return {
    ...(tipo ? { tipo } : {}),
    ...(centroDeCusto ? { centroDeCusto } : {}),
    ...(categoria.success ? { categoriaId: categoria.data } : {}),
    ...(origem ? { origem } : {}),
  };
}

/**
 * Escreve UM filtro na URL: o valor entra na chave, e "todos" (vazio ou
 * `undefined`) tira a chave. A chave é a da tela — `centro`, `categoria` —,
 * e um erro de digitação nela é erro de compilação, não filtro que nunca
 * pega.
 */
export function definirFiltro(
  params: URLSearchParams,
  chave: ChaveDeFiltro,
  valor: string | undefined,
): void {
  if (valor) params.set(chave, valor);
  else params.delete(chave);
}

export function temFiltroNoExtrato(filtros: FiltrosDoExtrato): boolean {
  return Boolean(filtros.tipo || filtros.centroDeCusto || filtros.categoriaId || filtros.origem);
}

/** Tira só os filtros: o mês e o modal aberto continuam na URL. */
export function limparFiltrosDoExtrato(params: URLSearchParams): void {
  for (const chave of CHAVES_DOS_FILTROS) params.delete(chave);
}

/** O que a consulta do extrato manda ao servidor: o mês, os filtros e a posição na lista. */
export function consultaDoExtrato(
  mes: string | undefined,
  filtros: FiltrosDoExtrato,
  cursor?: string,
): Partial<ConsultaDoExtrato> {
  return {
    ...(mes ? { mes } : {}),
    ...(filtros.tipo ? { tipo: filtros.tipo } : {}),
    ...(filtros.centroDeCusto ? { centroDeCusto: filtros.centroDeCusto } : {}),
    ...(filtros.categoriaId ? { categoriaId: filtros.categoriaId } : {}),
    ...(filtros.origem ? { origem: filtros.origem } : {}),
    ...(cursor ? { cursor } : {}),
    limite: LIMITE_DO_EXTRATO,
  };
}

// ---------------------------------------------------------------------
// O card "Resultado por centro de custo"
// ---------------------------------------------------------------------

/**
 * Os centros em que houve entrada ou saída REALIZADA no mês, na ordem em
 * que o servidor mandou. O servidor devolve sempre os quatro — a forma da
 * resposta não muda —, e esconder o que não se mexeu é daqui: uma linha de
 * R$ 0 em "Quadra 2" não informa nada.
 *
 * `undefined` é o servidor de antes deste campo (a tela nova chegou antes
 * do deploy do backend): o card fica vazio em vez de derrubar a página.
 */
export function centrosComMovimento(
  porCentro: readonly ResultadoDoCentro[] | undefined,
): ResultadoDoCentro[] {
  return (porCentro ?? []).filter((c) => c.entradas !== 0 || c.saidas !== 0);
}

/** A cor do resultado, só por token: verde no ganho, vermelho na perda, a do texto no zero. */
export function corDoResultado(resultado: number): string | undefined {
  if (resultado > 0) return 'var(--green)';
  if (resultado < 0) return 'var(--red-lt)';
  return undefined;
}

/**
 * Negativo com o sinal de menos tipográfico, como o resto da tela
 * ("− R$ 1.240"). Estorno de um mês anterior deixa uma saída negativa, e
 * o `Intl` a escreveria com hífen colado no símbolo da moeda.
 */
export function comSinal(centavos: number): string {
  return `${centavos < 0 ? '−' : ''}${dinheiro(Math.abs(centavos))}`;
}

// ---------------------------------------------------------------------
// A linha do extrato e a ficha: valor e situação
// ---------------------------------------------------------------------

/** O lançamento aberto, de `?lancamento=<id>` — só um id de verdade chega ao caminho da API. */
export function lerLancamentoDaUrl(params: URLSearchParams): string | undefined {
  const id = zId.safeParse(params.get('lancamento'));
  return id.success ? id.data : undefined;
}

/**
 * "+ R$ 120" quando o dinheiro entra, "− R$ 120" quando sai — pelo EFEITO
 * no caixa, e não pelo tipo: o estorno repete o tipo do original com o
 * valor negativo, e o efeito dele é o contrário do que a linha original
 * fez. Pelo tipo, o estorno de uma saída sairia "− -R$ 120", em vermelho.
 */
export function valorDoLancamento(l: Pick<LancamentoNoExtrato, 'tipo' | 'valor'>): string {
  const efeito = efeitoNoCaixa(l);
  return `${efeito < 0 ? '−' : '+'} ${dinheiro(Math.abs(efeito))}`;
}

/** A cor do efeito no caixa — a mesma regra do valor, só por token. */
export function corDoLancamento(
  l: Pick<LancamentoNoExtrato, 'tipo' | 'valor'>,
): string | undefined {
  return corDoResultado(efeitoNoCaixa(l));
}

/**
 * O que a linha É, para a coluna Situação e para a ficha. O estado que
 * fecha a linha vence a situação: um realizado estornado continua
 * `Realizado` no banco, mas o que a pessoa precisa ver é que ele foi
 * desfeito.
 */
export function situacaoDoLancamento(
  l: Pick<LancamentoNoExtrato, 'situacao' | 'estornoDe' | 'estornadoPor' | 'canceladoEm'>,
): { rotulo: string; situacao: Situacao } {
  if (l.canceladoEm) return { rotulo: 'Cancelado', situacao: 'neutra' };
  if (l.estornoDe) return { rotulo: 'Estorno', situacao: 'neutra' };
  if (l.estornadoPor) return { rotulo: 'Estornado', situacao: 'neutra' };
  return l.situacao === 'Realizado'
    ? { rotulo: 'Realizado', situacao: 'ok' }
    : { rotulo: 'Previsto', situacao: 'aguardando' };
}

// ---------------------------------------------------------------------
// As ações do lançamento
// ---------------------------------------------------------------------

export type AcaoDoLancamento = 'realizar' | 'editar' | 'estornar' | 'cancelar';

/**
 * Estorno, estornado ou cancelado: a linha já disse tudo o que tinha a
 * dizer e não muda mais. O que a ficha mostra, então, é o DESFECHO (quem
 * estornou, por quê, onde está o estorno) — e não o caminho de correção de
 * quem ainda está vivo.
 */
export function estaEncerrado(
  l: Pick<LancamentoNoExtrato, 'estornoDe' | 'estornadoPor' | 'canceladoEm'>,
): boolean {
  return Boolean(l.estornoDe || l.estornadoPor || l.canceladoEm);
}

type EstadoDoLancamento = Pick<
  LancamentoNoExtrato,
  'situacao' | 'origemTipo' | 'estornoDe' | 'estornadoPor' | 'canceladoEm'
>;

/**
 * O que a tela oferece para um lançamento, na ordem dos botões. É o
 * espelho do que o servidor aceita — o servidor decide de novo, e o que
 * ele recusa volta como 409 com o caminho certo —, e a tabela é esta:
 *
 *   estorno, estornado ou cancelado        nenhuma: a linha não muda mais
 *   automático de entrada de mercadoria,
 *     ainda previsto                       realizar (é a compra a prazo que vai ser paga)
 *   qualquer outro automático              nenhuma: a correção é feita na origem
 *   manual, previsto                       realizar, editar, cancelar
 *   manual, realizado                      editar, estornar
 *
 * O estado que fecha a linha é conferido ANTES do resto: um realizado
 * estornado seria `['editar', 'estornar']` se só se olhasse a situação.
 */
export function acoesDoLancamento(l: EstadoDoLancamento): AcaoDoLancamento[] {
  if (estaEncerrado(l)) return [];
  if (l.origemTipo) {
    return l.origemTipo === 'EntradaMercadoria' && l.situacao === 'Previsto' ? ['realizar'] : [];
  }
  return l.situacao === 'Previsto' ? ['realizar', 'editar', 'cancelar'] : ['editar', 'estornar'];
}

/**
 * A ação da matriz que cada ação do lançamento pede — a mesma do
 * `FinanceiroController`: realizar, editar e estornar são `editar` (o
 * estorno e a correção "não são uma quarta ação distinta na matriz"), e só
 * cancelar tem a ação própria.
 */
export const PERMISSAO_DA_ACAO: Readonly<Record<AcaoDoLancamento, Acao>> = {
  realizar: 'editar',
  editar: 'editar',
  estornar: 'editar',
  cancelar: 'cancelar',
};

/**
 * As ações do estado, filtradas pelo que o perfil alcança. COSMÉTICO: isto
 * esconde botão, não protege nada — quem decide é o servidor, e ele
 * confere de novo.
 */
export function acoesPermitidas(
  l: EstadoDoLancamento,
  pode: (recurso: Recurso, acao: Acao) => boolean,
): AcaoDoLancamento[] {
  return acoesDoLancamento(l).filter((acao) => pode('financeiro', PERMISSAO_DA_ACAO[acao]));
}

// ---------------------------------------------------------------------
// A origem do lançamento automático
// ---------------------------------------------------------------------

/**
 * Onde se corrige o que nasceu de outro módulo. O lançamento automático é
 * imutável pela tela — editá-lo quebraria a conciliação em silêncio — e o
 * caminho é desfazer o fato que o gerou. Exaustivo sobre `OrigemLancamento`:
 * a origem nova que esquecer a instrução não compila.
 */
const CORRECAO_NA_ORIGEM: Readonly<Record<OrigemLancamento, string>> = {
  Comanda: 'para corrigir, estorne a comanda no Bar',
  Fatura: 'desfaça o recebimento na fatura',
  EntradaMercadoria: 'estorne a entrada no Estoque',
  Marcacao: 'altere o pagamento na reserva',
  // O fechamento do caixa é definitivo (doc 21, "Em aberto"): não há o que
  // desfazer na origem, e a contagem errada se corrige com um lançamento
  // manual de ajuste.
  Caixa: 'o fechamento do caixa é definitivo; para corrigir, lance um ajuste manual',
};

/**
 * "Comanda fechada — para corrigir, estorne a comanda no Bar": o fato de
 * onde veio (o rótulo é o mesmo que o servidor usa ao recusar a edição) e o
 * caminho da correção.
 */
export function instrucaoDaOrigem(origem: OrigemLancamento): string {
  return `${rotuloDaOrigem(origem)} — ${CORRECAO_NA_ORIGEM[origem]}`;
}

/**
 * "Comanda fechada" — o rótulo do servidor, que é de meio de frase, com a
 * inicial maiúscula para a linha "Origem" da ficha.
 */
export function rotuloDaOrigem(origem: OrigemLancamento): string {
  const rotulo = ROTULO_DA_ORIGEM[origem];
  return `${rotulo.charAt(0).toUpperCase()}${rotulo.slice(1)}`;
}

// ---------------------------------------------------------------------
// Realizar: Previsto → Realizado
// ---------------------------------------------------------------------

/** O automático e a folha com detalhamento guardam o valor que têm; o resto pode diferir da previsão. */
const temDetalhamentoDaFolha = (l: { readonly folha?: readonly unknown[] }): boolean =>
  (l.folha?.length ?? 0) > 0;

/**
 * Onde a data começa: a do lançamento, mas nunca no futuro — o realizado é
 * fato, e o servidor recusa data adiante de hoje. A conta de energia
 * prevista para o mês que vem, realizada agora, começa em hoje.
 */
export function dataPadraoDaRealizacao(data: string, hoje: string): string {
  return data <= hoje ? data : hoje;
}

/** A mesma conferência do servidor, para dizer antes de ir: a data existe e não é futura. */
export function erroDaDataDaRealizacao(data: string, hoje: string): string | undefined {
  if (!dataValida(data)) return 'Informe uma data válida.';
  if (data > hoje) return 'Realizado não tem data no futuro: use a de hoje ou uma anterior.';
  return undefined;
}

/**
 * As formas que o seletor oferece: as quatro de sempre, e a que o
 * lançamento já tem se estiver fora delas — o seletor não pode mostrar
 * "Pix" para quem tem outra e, sem querer, trocá-la ao salvar.
 */
export function opcoesDeForma(atual: FormaPagamento | null): FormaPagamento[] {
  const manuais: FormaPagamento[] = [...FORMAS_DO_LANCAMENTO_MANUAL];
  return atual && !manuais.includes(atual) ? [atual, ...manuais] : manuais;
}

/**
 * O valor se digita ao realizar só no lançamento manual sem detalhamento
 * de folha. O automático vem da origem (outro valor é 400), e o valor da
 * folha detalhada é a soma dos itens (outro valor é 409).
 */
export function valorEditavelNaRealizacao(
  l: Pick<DetalheDoLancamento, 'origemTipo' | 'folha'>,
): boolean {
  return !l.origemTipo && !temDetalhamentoDaFolha(l);
}

/**
 * O corpo da realização: só o que difere do lançamento. Mandar o que a
 * pessoa nem tocou é sobrescrever a previsão com o que a tela tinha — e,
 * na data, é o que decide entre a do lançamento (que o servidor já tem) e
 * a de hoje, quando o previsto estava no futuro.
 */
export function corpoDaRealizacao(
  atual: Pick<LancamentoNoExtrato, 'data' | 'valor' | 'formaPagamento'>,
  novo: {
    readonly data: string;
    /** Centavos; `undefined` quando o valor não se digita. */
    readonly valor?: number;
    /** `''` é "não informada": não vai. */
    readonly formaPagamento: FormaPagamento | '';
  },
): RealizacaoDeLancamento {
  return {
    ...(novo.data !== atual.data ? { data: novo.data } : {}),
    ...(novo.valor !== undefined && novo.valor !== atual.valor ? { valor: novo.valor } : {}),
    ...(novo.formaPagamento !== '' && novo.formaPagamento !== atual.formaPagamento
      ? { formaPagamento: novo.formaPagamento }
      : {}),
  };
}

// ---------------------------------------------------------------------
// Editar um lançamento manual
// ---------------------------------------------------------------------

/**
 * O que a edição deixa mexer: valor e data são do PREVISTO — o fato não
 * muda, estorna-se e lança-se de novo — e o valor da folha detalhada é a
 * soma dos itens. `true` é "pode editar".
 */
export function restricoesDaEdicao(l: Pick<DetalheDoLancamento, 'situacao' | 'folha'>): {
  valor: boolean;
  data: boolean;
} {
  const previsto = l.situacao === 'Previsto';
  return { valor: previsto && !temDetalhamentoDaFolha(l), data: previsto };
}

/**
 * As contas que o seletor da edição oferece: as do TIPO da linha — o tipo
 * não muda —, e a conta da própria linha SEMPRE, no topo quando a lista não
 * a traz (inativa, ou ainda carregando). Sem isso o seletor cairia na
 * primeira conta da lista, e salvar trocaria a categoria sem a pessoa ter
 * mexido nela.
 */
export function categoriasParaEdicao(
  categorias: readonly CategoriaDoPlano[],
  atual: Pick<LancamentoNoExtrato, 'tipo' | 'categoriaId' | 'categoria'>,
): { id: string; nome: string }[] {
  const doTipo = categoriasParaNovoLancamento(categorias)
    .filter((c) => c.tipo === atual.tipo)
    .map((c) => ({ id: c.id, nome: c.nome }));
  return doTipo.some((c) => c.id === atual.categoriaId)
    ? doTipo
    : [{ id: atual.categoriaId, nome: atual.categoria }, ...doTipo];
}

/**
 * As contas que o SISTEMA lança sozinho, e que ninguém escolhe à mão:
 * "Quebra de caixa" e "Sobra de caixa" nascem do fechamento do caixa (21).
 * O seletor do lançamento manual lista as categorias da API, e sem este
 * filtro elas apareceriam ali. O extrato e os filtros continuam mostrando.
 */
export const CHAVES_DE_CONTAS_DO_SISTEMA: readonly string[] = ['quebra_caixa', 'sobra_caixa'];

export function categoriasParaNovoLancamento(
  categorias: readonly CategoriaDoPlano[],
): CategoriaDoPlano[] {
  return categorias.filter((c) => !CHAVES_DE_CONTAS_DO_SISTEMA.includes(c.chave));
}

// ---------------------------------------------------------------------
// Caixa da recepção — #v-caixa (documento 21)
// ---------------------------------------------------------------------

/**
 * O contado contra o esperado: `falta` quando a gaveta tem menos (vira
 * "Quebra de caixa"), `sobra` quando tem mais, `confere` quando bate.
 * `valor` é sempre o módulo, em centavos.
 */
export function diferencaDoCaixa(
  esperado: number,
  contado: number,
): { valor: number; tipo: 'falta' | 'sobra' | 'confere' } {
  const delta = contado - esperado;
  if (delta === 0) return { valor: 0, tipo: 'confere' };
  return { valor: Math.abs(delta), tipo: delta < 0 ? 'falta' : 'sobra' };
}

/**
 * O corpo do fechamento, ou o erro que a tela mostra antes de enviar.
 *
 * Sem diferença, `justificativa` fica FORA do corpo: o servidor responde 400
 * para texto em branco mesmo quando não há o que justificar. Com diferença,
 * ela é obrigatória (aparada, 3 caracteres). `esperadoConferido` vai sempre:
 * é o esperado que a pessoa viu, e se mudou no meio o servidor responde 409.
 */
export function corpoDoFechamentoDeCaixa(entrada: {
  esperado: number;
  contado: number | null;
  justificativa: string;
}):
  | { corpo: { contado: number; esperadoConferido: number; justificativa?: string } }
  | { erro: string } {
  if (entrada.contado === null) return { erro: 'Informe quanto há na gaveta.' };
  const { valor, tipo } = diferencaDoCaixa(entrada.esperado, entrada.contado);
  const base = { contado: entrada.contado, esperadoConferido: entrada.esperado };
  if (tipo === 'confere') return { corpo: base };
  const justificativa = entrada.justificativa.trim();
  if (justificativa.length < 3) {
    return { erro: `Diga o que explica a diferença de ${dinheiro(valor)}.` };
  }
  return { corpo: { ...base, justificativa } };
}

/** Sangria e suprimento: valor positivo e motivo de ao menos 3 caracteres (o servidor repete). */
export function erroDoMovimentoDeCaixa(valor: number | null, motivo: string): string | undefined {
  if (valor === null || valor <= 0) return 'Informe o valor.';
  if (motivo.trim().length < 3) return 'Diga o motivo do movimento.';
  return undefined;
}

/**
 * O dinheiro recebido sem caixa aberto não é bloqueado — só vai para
 * "dinheiro fora do caixa" (o mesmo vale para a data anterior à abertura do
 * caixa; a decisão aqui é só pela existência do caixa, e o texto do aviso diz
 * as duas coisas). `caixaAberto` indefinido (carregando, ou o
 * perfil não pode ver o caixa) não avisa: não se adivinha.
 */
export function avisoDeDinheiroForaDoCaixa(
  forma: string | null | undefined,
  caixaAberto: boolean | undefined,
): boolean {
  return forma === 'Dinheiro' && caixaAberto === false;
}

/** "14:05" — a hora do instante, como `horaDaAbertura` da comanda (fuso do navegador). */
export function horaDoInstante(iso: string): string {
  return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

export interface EntradaDaEdicao {
  readonly categoriaId: string;
  readonly descricao: string;
  /** O texto do campo: vazio apaga a subcategoria. */
  readonly subcategoria: string;
  /** Centavos; `undefined` quando o campo não é editável ou o texto não é valor. */
  readonly valor?: number;
  readonly data: string;
  readonly centroDeCusto: CentroDeCusto;
  /** `''` é "não informada": não limpa a forma que o lançamento tem. */
  readonly formaPagamento: FormaPagamento | '';
}

/**
 * O corpo do PATCH: só o que mudou. É o que o `zAlteracaoDeLancamento`
 * pede — "Nada para alterar." quando não sobra campo —, e é o que evita
 * mandar valor e data de um realizado, que o servidor recusa.
 *
 * Subcategoria apagada vai como `null`: é assim que o servidor a limpa.
 */
export function alteracaoDoLancamento(
  atual: Pick<
    LancamentoNoExtrato,
    | 'categoriaId'
    | 'descricao'
    | 'subcategoria'
    | 'valor'
    | 'data'
    | 'centroDeCusto'
    | 'formaPagamento'
  >,
  novo: EntradaDaEdicao,
  editavel: { readonly valor: boolean; readonly data: boolean },
): AlteracaoDeLancamento {
  const descricao = novo.descricao.trim();
  const subcategoria = novo.subcategoria.trim() || null;
  return {
    ...(novo.categoriaId !== atual.categoriaId ? { categoriaId: novo.categoriaId } : {}),
    ...(descricao !== atual.descricao ? { descricao } : {}),
    ...(subcategoria !== atual.subcategoria ? { subcategoria } : {}),
    ...(editavel.valor && novo.valor !== undefined && novo.valor !== atual.valor
      ? { valor: novo.valor }
      : {}),
    ...(editavel.data && novo.data !== atual.data ? { data: novo.data } : {}),
    ...(novo.centroDeCusto !== atual.centroDeCusto ? { centroDeCusto: novo.centroDeCusto } : {}),
    ...(novo.formaPagamento !== '' && novo.formaPagamento !== atual.formaPagamento
      ? { formaPagamento: novo.formaPagamento }
      : {}),
  };
}

/**
 * Centavos → o texto que a pessoa digitaria no campo ("7200,00"). O inverso
 * de `paraCentavos`: o que entra preenchido sai igual ao que o servidor
 * tem, e só vira "mudança" se a pessoa mexer.
 */
export function valorNoCampo(centavos: number): string {
  return (centavos / 100).toFixed(2).replace('.', ',');
}

// ---------------------------------------------------------------------
// Estornar e cancelar: o motivo
// ---------------------------------------------------------------------

/**
 * O campo de motivo do estorno e o do cancelamento: os mesmos limites do
 * schema do servidor (3 a 200, sem os espaços das pontas), lidos DELE — o
 * número não é copiado para cá, e por isso não deriva. A mensagem do mínimo
 * é a do schema ("Diga por que…"); a do máximo é daqui, porque o zod a
 * escreve em inglês.
 *
 * Fica em `financeiro` e não reusa `motivoValido` de `estoque/formatos`:
 * aquele é do cancelamento de comanda, e o razão não importa do bar.
 */
const ESQUEMA_DO_MOTIVO = {
  estornar: zEstornoDeLancamento.shape.motivo,
  cancelar: zCancelamentoDeLancamento.shape.motivo,
} as const;

export function erroDoMotivo(acao: 'estornar' | 'cancelar', texto: string): string | undefined {
  const resultado = ESQUEMA_DO_MOTIVO[acao].safeParse(texto);
  if (resultado.success) return undefined;
  const problema = resultado.error.issues[0];
  if (problema?.code === 'too_big') return `No máximo ${problema.maximum} caracteres.`;
  return problema?.message;
}

/**
 * O aviso do estorno. O estorno leva a data de HOJE — o mês que já fechou
 * continua dizendo o que disse —, e quem estorna olhando agosto não o
 * encontra na lista de agosto: o aviso diz onde ele entrou.
 */
export function textoDoEstorno(estorno: Pick<LancamentoNoExtrato, 'data'>): string {
  return `Lançamento estornado. O estorno entrou no razão em ${dataCompleta(estorno.data)}.`;
}

/** O aviso do cancelamento: numa recorrência, "esta e as próximas" cancela mais de uma. */
export function textoDoCancelamento(cancelados: number): string {
  return cancelados === 1 ? 'Previsão cancelada.' : `${cancelados} previsões canceladas.`;
}

// ---------------------------------------------------------------------
// A ficha: recorrência e comprovante
// ---------------------------------------------------------------------

/** "Mensal · desde 07/09/2026", e — se a regra acabou — "· até 07/03/2027 · encerrada". */
export function descricaoDaRecorrencia(
  r: Pick<RecorrenciaResumida, 'frequencia' | 'vigenciaInicio' | 'vigenciaFim' | 'encerradaEm'>,
): string {
  return [
    ROTULO_FREQUENCIA[r.frequencia],
    `desde ${dataCompleta(r.vigenciaInicio)}`,
    ...(r.vigenciaFim ? [`até ${dataCompleta(r.vigenciaFim)}`] : []),
    ...(r.encerradaEm ? ['encerrada'] : []),
  ].join(' · ');
}

/** "117 KB", "1,4 MB" — o comprovante na grandeza que se lê. */
export function tamanhoDoArquivo(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (Math.round(kb) < 1024) return `${Math.round(kb)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}
